/**
 * Campaigns under one ad account: listed from TikTok, created DISABLE, switched on
 * and off.
 *
 * What this screen decides lives in lib/campaigns.ts and what it says in
 * lib/campaign-copy.ts, where the runner reaches them; this file wires them to the
 * API and renders. Creating and switching are offered only where the caller can
 * write, and the worker refuses the rest whatever is shown.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AdGroups } from "../components/AdGroups.tsx";
import { statusOf } from "../lib/api-error.ts";
import {
	type Campaign,
	type CampaignPage,
	type CampaignSwitch,
	type Grant,
	type Standing,
	UnauthorizedError,
	api,
} from "../lib/api.ts";
import {
	CREATE_ANYWAY,
	CREATE_LOCKED,
	CREATE_LOCK_IS_LOCAL,
	EXISTS,
	FLOOR_BANNER,
	NOT_REPORTED,
	READ_ONLY,
	UNKNOWN,
	budgetText,
	createdText,
	enablePrompt,
	hostWarning,
	mismatchText,
	pagerLabel,
} from "../lib/campaign-copy.ts";
import {
	BUDGET_MODES,
	type CampaignDraft,
	type CreateOutcome,
	type FoundById,
	OBJECTIVES,
	SPECIAL_INDUSTRIES,
	campaignRequest,
	canWriteAccount,
	createOutcome,
	createThrew,
	foundById,
	mayCreateAgain,
	outcomeAfterReload,
	statusOutcome,
} from "../lib/campaigns.ts";
import { tiktokHost } from "../lib/tiktok-host.ts";
import { type Load, useLoad } from "../lib/useLoad.ts";
import {
	Badge,
	Button,
	InfoBanner,
	Input,
	Page,
	Select,
	Table,
	fieldHelperClass,
	fieldLabelClass,
} from "../ui/index.ts";

type Listing = { campaigns: Campaign[]; page: CampaignPage; truncated: boolean };

const EMPTY: CampaignDraft = {
	name: "",
	objectiveType: "TRAFFIC",
	budgetMode: "BUDGET_MODE_DAY",
	budget: "",
	specialIndustries: [],
};

const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function Campaigns({ standing }: { standing: Standing }) {
	const [accounts] = useLoad(() => api.tiktokAccounts());
	// A platform seat's grants are not on standing, and only a manager's decide
	// anything: an admin holds every account and a user writes none.
	const [seat] = useLoad<Grant[] | null>(() =>
		standing.standing === "platform" && standing.platform?.role === "manager"
			? api.myAccess().then((m) => m.grants)
			: Promise.resolve(null),
	);
	// Where a write goes, named on the page, beside Create and in the ENABLE prompt. Unknown
	// until the smoke check answers, and treated as production until then.
	const [smoke] = useLoad(() => api.smoke());
	const host = tiktokHost(smoke.state === "ready" ? smoke.data.tiktok?.target : null);
	const warning = hostWarning(host);

	const list = accounts.state === "ready" ? accounts.data.accounts : [];
	const [advertiser, setAdvertiser] = useState("");
	const account = list.find((a) => a.advertiserId === advertiser) ?? null;
	const writable =
		account !== null &&
		canWriteAccount(standing, seat.state === "ready" ? seat.data : null, account.id);
	const currency = account?.currency ?? null;

	const [listing, setListing] = useState<Load<Listing> | null>(null);
	/*
	 * Which campaign's ad groups are open, by id and name. Null means not asked — a
	 * different answer from "none", and `adgroup-copy.ts` keeps the two sentences apart.
	 * The name is carried rather than looked up again: the list can page away from the
	 * row that opened the section, and a heading that emptied on paging would read as the
	 * campaign having vanished.
	 */
	const [opened, setOpened] = useState<{ id: string; name: string } | null>(null);

	// Which page of the account is on screen: TikTok pages campaign/get, so the screen asks for
	// one page rather than reading the first 1000 and calling that the account.
	const [wanted, setWanted] = useState(1);
	// An answer for an account no longer chosen is dropped rather than shown.
	const chosen = useRef(advertiser);
	chosen.current = advertiser;
	/** Resolves true only when this account's list was actually read. */
	const fetchListing = useCallback((adv: string, page: number): Promise<boolean> => {
		if (!adv) {
			setListing(null);
			return Promise.resolve(false);
		}
		setListing({ state: "loading" });
		return api.campaigns(adv, { page }).then(
			(data) => {
				if (chosen.current !== adv) return false;
				setListing({ state: "ready", data });
				return true;
			},
			(e) => {
				if (chosen.current !== adv || e instanceof UnauthorizedError) return false;
				setListing({ state: "failed", why: messageOf(e), status: statusOf(e) });
				return false;
			},
		);
	}, []);
	useEffect(() => {
		void fetchListing(advertiser, wanted);
	}, [advertiser, wanted, fetchListing]);
	const reload = () => void fetchListing(advertiser, wanted);

	/** The row shows what TikTok read back, not what was asked. */
	function showReadBack(id: string, operationStatus: string, secondaryStatus: string | null) {
		setListing((l) =>
			l?.state === "ready"
				? {
						state: "ready",
						data: {
							...l.data,
							campaigns: l.data.campaigns.map((c) =>
								c.id === id ? { ...c, operationStatus, secondaryStatus } : c,
							),
						},
					}
				: l,
		);
	}

	const [note, setNote] = useState<{ title: string; body?: string } | null>(null);
	const [switching, setSwitching] = useState<string | null>(null);

	async function flip(c: Campaign, to: CampaignSwitch) {
		if (!account) return;
		// Turning a campaign on is the one step after which TikTok can start delivering, so
		// it is confirmed with the campaign, the account and the budget in front of the person.
		// Turning one off asks nothing.
		if (to === "ENABLE" && !window.confirm(enablePrompt(c, account, currency, host))) return;
		setSwitching(c.id);
		setNote(null);
		try {
			const { status, body } = await api.setCampaignStatus(c.id, {
				advertiser: account.advertiserId,
				status: to,
			});
			const o = statusOutcome(to, status, body);
			if (o.kind === "set") showReadBack(c.id, o.operationStatus, o.secondaryStatus);
			else if (o.kind === "refused") setNote({ title: o.error });
			else {
				setNote({ title: mismatchText(o), body: o.error });
				if (o.operationStatus) showReadBack(c.id, o.operationStatus, o.secondaryStatus);
				else reload();
			}
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			// Whether the write landed is not known: read the list again rather than guess.
			setNote({ title: messageOf(e) });
			reload();
		} finally {
			setSwitching(null);
		}
	}

	const [draft, setDraft] = useState<CampaignDraft>(EMPTY);
	const [formError, setFormError] = useState<string | null>(null);
	const [outcome, setOutcome] = useState<CreateOutcome | null>(null);
	const [creating, setCreating] = useState(false);
	const edit = (patch: Partial<CampaignDraft>) => setDraft((d) => ({ ...d, ...patch }));

	async function create(e: React.FormEvent) {
		e.preventDefault();
		if (outcome && !mayCreateAgain(outcome)) return;
		setFormError(null);
		setOutcome(null);
		const request = campaignRequest(advertiser, draft);
		if (!request.ok) {
			setFormError(request.error);
			return;
		}
		setCreating(true);
		try {
			const { status, body } = await api.createCampaign(request.value);
			const o = createOutcome(status, body);
			setOutcome(o);
			if (o.kind === "created") setDraft(EMPTY);
			// Anything but a refusal may have made a campaign: read the list again.
			if (o.kind !== "refused") reload();
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setOutcome(createThrew(messageOf(err)));
			reload();
		} finally {
			setCreating(false);
		}
	}

	// After `exists` or `unknown` the draft stays filled in, so it can be looked up by name, and
	// Create stays locked after an answer that may have made a campaign. What lifts
	// it is in `outcomeAfterReload`: a read by id for an answer that carried one, and
	// for the rest only the deliberate "create anyway" below — never a list, which
	// may not show a new campaign yet. Kept here in the component, so a browser
	// reload clears it; the copy says so rather than leaving that to be found by
	// pressing F5.
	const locked = outcome !== null && !mayCreateAgain(outcome);
	// The second step of "create anyway": the sentence to agree to is ours, so the
	// dialog has to be ours too. Cleared whenever the lock is.
	const [confirming, setConfirming] = useState(false);
	// Whatever lifted the lock — a read by id, a new create, "create anyway" itself —
	// the half-made confirmation goes with it, so the next lock does not open already
	// agreed to.
	useEffect(() => {
		if (!locked) setConfirming(false);
	}, [locked]);
	const [reloading, setReloading] = useState(false);

	/**
	 * Reload the rows, and — where there is an id — ask TikTok about that one campaign.
	 *
	 * 🔴 The list does not decide the lock any more. A list that came back is not a
	 * campaign that is absent: on X the list was measured lagging behind a create by
	 * minutes (twads#86), and for TikTok nobody has measured it at all, so "the list
	 * came back without it" establishes nothing either way. Deciding on it is what let
	 * a second campaign be created (twads#87). The read by id is the only thing
	 * consulted here.
	 */
	async function reloadAndUnlock() {
		// The answer this run is about. Everything below is applied only if it is still
		// the one on screen: an await sits in the middle, and clearing a lock that a
		// later create put there would be the same bug one step along.
		const about = outcome;
		const asked = advertiser;
		setReloading(true);
		try {
			await fetchListing(asked, wanted);
			const id = about?.kind === "exists" ? about.id : null;
			const byId: FoundById = id
				? await api.campaign(id, asked).then(
						({ status, body }) => foundById(status, body, id),
						// A read that did not come back establishes nothing — and "nothing
						// established" has to keep the lock, not lift it.
						() => "unasked",
					)
				: "unasked";
			setOutcome((o) => (o === about ? outcomeAfterReload(o, byId) : o));
			// The lock lifts only when TikTok confirmed the campaign by id, and then the
			// draft describes something that certainly exists. Leaving it filled in is how
			// the screen offers to make it twice.
			if (byId === "found" && outcomeAfterReload(about, byId) === null) setDraft(EMPTY);
		} finally {
			setReloading(false);
		}
	}

	function choose(next: string) {
		setAdvertiser(next);
		// Closed in the handler rather than by an effect watching `advertiser`: the open id
		// belongs to the previous ad account, and reading it under the new one answers 404 —
		// a true answer to a question nobody asked. An effect here would have listed
		// `advertiser` as a dependency it never reads, which is a signal dressed as a read.
		setOpened(null);
		setWanted(1);
		setNote(null);
		setOutcome(null);
		setFormError(null);
	}

	return (
		<Page
			title="Campaigns"
			description="Read from TikTok, one ad account at a time. A campaign created here starts switched off."
			actions={<Badge variant={host.kind === "sandbox" ? "info" : "warning"}>{host.text}</Badge>}
		>
			<div className="space-y-4">
				{accounts.state === "failed" && (
					<InfoBanner variant="danger" title="Ad accounts could not be read.">
						{accounts.why}
					</InfoBanner>
				)}
				<div className="max-w-md">
					<Select
						label="Ad account"
						placeholder={accounts.state === "loading" ? "Loading…" : "Choose an ad account"}
						value={advertiser}
						disabled={list.length === 0}
						onChange={(e) => choose(e.target.value)}
						options={list.map((a) => ({
							value: a.advertiserId,
							label: `${a.name} · ${a.advertiserId}`,
						}))}
					/>
				</div>
				{accounts.state === "ready" && list.length === 0 && (
					<InfoBanner variant="info" title="No ad account to choose.">
						Campaigns are listed under an ad account. One appears here once it is synced and you
						hold it.
					</InfoBanner>
				)}

				{note && (
					<InfoBanner variant="danger" title={note.title}>
						{note.body}
					</InfoBanner>
				)}
				{account && !writable && seat.state !== "loading" && (
					<InfoBanner variant="info" title={READ_ONLY} />
				)}
				{listing?.state === "ready" && listing.data.truncated && (
					<InfoBanner variant="warning" title={FLOOR_BANNER.title}>
						{FLOOR_BANNER.body}
					</InfoBanner>
				)}
				{listing?.state === "failed" && (
					<InfoBanner variant="danger" title="Campaigns could not be read.">
						{listing.why}
					</InfoBanner>
				)}

				{account && (
					<Table<Campaign>
						columns={[
							{
								id: "name",
								header: "Campaign",
								accessor: (c) => (
									<>
										<div className="font-medium text-content">{c.name}</div>
										<div className="font-mono text-xs text-muted">{c.id}</div>
									</>
								),
							},
							{
								id: "adgroups",
								header: "Ad groups",
								accessor: (c) => (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setOpened({ id: c.id, name: c.name })}
									>
										{opened?.id === c.id ? "Reading" : "Read"}
									</Button>
								),
							},
							{
								id: "switch",
								header: "Switch",
								accessor: (c) => (
									<div className="flex items-center gap-2">
										<Badge variant={c.operationStatus === "ENABLE" ? "success" : "default"}>
											{c.operationStatus}
										</Badge>
										{writable && (
											<Button
												variant="ghost"
												size="sm"
												loading={switching === c.id}
												disabled={switching !== null}
												onClick={() =>
													void flip(c, c.operationStatus === "ENABLE" ? "DISABLE" : "ENABLE")
												}
											>
												{c.operationStatus === "ENABLE" ? "Turn off" : "Turn on"}
											</Button>
										)}
									</div>
								),
							},
							{
								id: "delivery",
								header: "Delivery",
								accessor: (c) => (
									<span className="font-mono text-xs">{c.secondaryStatus ?? NOT_REPORTED}</span>
								),
							},
							{
								id: "objective",
								header: "Objective",
								accessor: (c) => <span className="font-mono text-xs">{c.objectiveType}</span>,
							},
							{
								id: "budget",
								header: "Budget",
								accessor: (c) => (
									<span className="font-mono text-xs">{budgetText(c, currency)}</span>
								),
							},
							{
								id: "created",
								header: "Created (TikTok)",
								accessor: (c) => <span className="text-muted">{c.createTime}</span>,
							},
						]}
						data={listing?.state === "ready" ? listing.data.campaigns : []}
						keyExtractor={(c) => c.id}
						failed={listing?.state === "failed"}
						loading={listing?.state === "loading"}
						emptyMessage="TikTok lists no campaigns under this ad account."
					/>
				)}

				{account && listing?.state === "ready" && (
					<div className="flex flex-wrap items-center gap-3">
						<Button
							variant="outline"
							size="sm"
							disabled={wanted <= 1}
							onClick={() => setWanted((p) => Math.max(1, p - 1))}
						>
							Previous
						</Button>
						<span className="text-xs text-muted">{pagerLabel(listing.data.page)}</span>
						<Button
							variant="outline"
							size="sm"
							disabled={wanted >= listing.data.page.totalPages}
							onClick={() => setWanted((p) => p + 1)}
						>
							Next
						</Button>
					</div>
				)}

				{account && (
					<AdGroups
						key={opened?.id ?? "none"}
						advertiser={advertiser}
						campaignId={opened?.id ?? null}
						campaignName={opened?.name ?? null}
					/>
				)}

				{account && writable && (
					<section className="border-t border-line pt-6">
						<h2 className="text-sm font-semibold text-content">New campaign</h2>
						<p className="mt-1 text-xs text-muted">
							Always created switched off (DISABLE). Turn it on in the list above once it is ready.
						</p>
						<div className="mt-4 space-y-3">
							{warning && (
								<InfoBanner variant="warning" title={warning.title}>
									{warning.body}
								</InfoBanner>
							)}
							{outcome && <CreateResult outcome={outcome} />}
							{formError && <InfoBanner variant="danger" title={formError} />}
						</div>
						<form className="mt-4 grid max-w-2xl gap-4 sm:grid-cols-2" onSubmit={create}>
							<Input
								label="Name"
								value={draft.name}
								onChange={(e) => edit({ name: e.target.value })}
							/>
							<Select
								label="Objective"
								value={draft.objectiveType}
								onChange={(e) => edit({ objectiveType: e.target.value })}
								options={OBJECTIVES.map((o) => ({ value: o, label: o }))}
							/>
							<Select
								label="Budget mode"
								value={draft.budgetMode}
								onChange={(e) => edit({ budgetMode: e.target.value })}
								options={BUDGET_MODES.map((m) => ({ value: m, label: m }))}
							/>
							{draft.budgetMode !== "BUDGET_MODE_INFINITE" && (
								<Input
									label={`Budget${currency ? ` (${currency})` : ""}`}
									inputMode="decimal"
									value={draft.budget}
									onChange={(e) => edit({ budget: e.target.value })}
									helperText="Sent to TikTok as typed, in the account's currency — not converted."
								/>
							)}
							<fieldset className="flex flex-col gap-1.5 sm:col-span-2">
								<legend className={fieldLabelClass}>Special industries</legend>
								<div className="flex flex-wrap gap-4">
									{SPECIAL_INDUSTRIES.map((s) => (
										<label key={s} className="flex items-center gap-2 text-sm text-content">
											<input
												type="checkbox"
												checked={draft.specialIndustries.includes(s)}
												onChange={(e) =>
													edit({
														specialIndustries: SPECIAL_INDUSTRIES.filter((v) =>
															v === s ? e.target.checked : draft.specialIndustries.includes(v),
														),
													})
												}
											/>
											{s}
										</label>
									))}
								</div>
								<p className={fieldHelperClass}>
									A legal declaration to TikTok. With none ticked the field is not sent, and
									TikTok's own default applies.
								</p>
							</fieldset>
							<div className="flex flex-wrap items-center gap-3 sm:col-span-2">
								<Button type="submit" loading={creating} disabled={locked}>
									Create switched off
								</Button>
								{locked && (
									<Button
										type="button"
										variant="outline"
										loading={reloading}
										onClick={() => void reloadAndUnlock()}
									>
										Reload list
									</Button>
								)}
							</div>
							{locked && (
								<div className={`${fieldHelperClass} sm:col-span-2 grid gap-2`}>
									<p>
										{CREATE_LOCKED} {CREATE_LOCK_IS_LOCAL}
									</p>
									{/*
									 * The way out where no read can settle it — its own two steps rather
									 * than `window.confirm`, because the browser names the buttons "OK"
									 * and "Cancel" and the sentence that has to be agreed to is
									 * `CREATE_ANYWAY.confirm`. A dialog whose wording we do not control
									 * cannot carry it.
									 *
									 * And Create stays a third, separate press: a single button that
									 * both unlocks and creates is the one pressed while still reading.
									 */}
									{!confirming ? (
										<span>
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => setConfirming(true)}
											>
												{CREATE_ANYWAY.label}
											</Button>
										</span>
									) : (
										<span className="flex flex-wrap items-center gap-2">
											<span className="text-sm text-danger">
												<strong>{CREATE_ANYWAY.title}</strong> {CREATE_ANYWAY.body}
											</span>
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => {
													setConfirming(false);
													setOutcome(null);
													setNote(null);
												}}
											>
												{CREATE_ANYWAY.confirm}
											</Button>
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => setConfirming(false)}
											>
												Keep the warning
											</Button>
										</span>
									)}
								</div>
							)}
						</form>
					</section>
				)}
			</div>
		</Page>
	);
}

/** Four answers, four different pieces of news; the two after which a retry is wrong stand apart. */
function CreateResult({ outcome }: { outcome: CreateOutcome }) {
	if (outcome.kind === "created")
		return <InfoBanner variant="success" title={createdText(outcome)} />;
	if (outcome.kind === "exists") {
		return (
			<InfoBanner variant="warning" title={EXISTS.title}>
				<p>{EXISTS.body}</p>
				{outcome.id && <p className="mt-1 font-mono">id {outcome.id}</p>}
				{outcome.campaign && (
					<p className="mt-1">TikTok reports it {outcome.campaign.operationStatus}.</p>
				)}
				<p className="mt-1">{outcome.error}</p>
			</InfoBanner>
		);
	}
	if (outcome.kind === "unknown") {
		return (
			<InfoBanner variant="warning" title={UNKNOWN.title}>
				<p>{UNKNOWN.body}</p>
				<p className="mt-1">{outcome.error}</p>
			</InfoBanner>
		);
	}
	return <InfoBanner variant="danger" title={outcome.error} />;
}
